import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import PropTypes from "prop-types";
import { confirmPasswordResetValidation } from "../../../utility/validation/passwordResetValidation";
import { IConfirmPasswordResetPayload } from "../../../utility/types/userType";
import { colors } from "../../../utility/types/constant";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { confirmPassWordReset } from "../../../redux/actions/userActions";
import { useNavigate } from "react-router-dom";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";

interface VerifyAndResetPasswordModalProps {
  open: boolean;
  handleClose: () => void;
}

const VerifyAndResetPasswordModal = (
  props: VerifyAndResetPasswordModalProps
) => {
  const navigate = useNavigate();

  const selectedUserData = useSelector(selectCurrentUser);

  const dispatch = useDispatch<AppDispatch>();

  const { open, handleClose } = props;

  const [successAlert, setSuccessAlert] = useState(false);

  const [errorAlert, setErrorAlert] = useState("");

  const initialValues: IConfirmPasswordResetPayload = {
    email: "",
    code: "",
    newPassword: "",
  };

  const handleCloseSuccessAlert = () => {
    setSuccessAlert(false);
  };

  const handleCloseErrorAlert = () => {
    setErrorAlert(""); // Set the error message to an empty string to hide the alert
  };

  const handleSubmit = async (userData: IConfirmPasswordResetPayload) => {
    try {
      await dispatch(confirmPassWordReset(userData)).unwrap();

      setSuccessAlert(true);

      setTimeout(() => {
        navigate(`user/${selectedUserData?._id}`);
      }, 5000);
    } catch (err: any) {
      setSuccessAlert(false);
      setErrorAlert(err.message || `Unknown ERROR occurred.`);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogContent>
        <Grid container direction="column" justifyContent="center" spacing={2}>
          <Grid
            item
            xs={12}
            container
            alignItems="center"
            justifyContent="center"
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontSize: "2rem" }}>
                Password Reset
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 4,
          }}
        >
          <Container maxWidth="lg">
            {successAlert && (
              <Stack sx={{ width: "100%" }} spacing={2}>
                <Alert severity="success" onClose={handleCloseSuccessAlert}>
                  Success - <strong>Your Password Reset Successful.</strong>
                </Alert>
              </Stack>
            )}

            {errorAlert && (
              <Stack sx={{ width: "100%" }} spacing={2}>
                <Alert severity="error" onClose={handleCloseErrorAlert}>
                  Error -{" "}
                  <strong>
                    {errorAlert
                      ? errorAlert
                      : "Email doesn't exist in our records or wrong code."}
                  </strong>
                </Alert>
              </Stack>
            )}

            <Stack spacing={3}>
              <div>
                <Grid container justifyContent="center" spacing={3}>
                  <Grid item xs={12} md={6} lg={8} sx={{ padding: 1 }}>
                    <Formik
                      initialValues={initialValues}
                      onSubmit={handleSubmit}
                      validationSchema={confirmPasswordResetValidation}
                    >
                      {({ values, handleChange, errors, touched }) => (
                        <Form>
                          <Card>
                            <CardHeader
                              subheader="Enter verification code and your new password"
                              title="Verify Code & Reset Password"
                            />
                            <CardContent>
                              <Box sx={{ mt: 3 }}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      label="Verification Code"
                                      name="code"
                                      autoComplete="off"
                                      onChange={handleChange}
                                      value={values.code}
                                      error={!!(errors?.code && touched?.code)}
                                      helperText={
                                        errors.code &&
                                        touched.code &&
                                        errors.code
                                      }
                                      sx={{
                                        fontSize: "1.2rem",
                                        "& .MuiOutlinedInput-root": {
                                          "& fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                          "&:hover fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                        },
                                      }}
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      label="Email"
                                      name="email"
                                      autoComplete="off"
                                      onChange={handleChange}
                                      value={values.email}
                                      error={
                                        !!(errors?.email && touched?.email)
                                      }
                                      helperText={
                                        errors.email &&
                                        touched.email &&
                                        errors.email
                                      }
                                      sx={{
                                        fontSize: "1.2rem",
                                        "& .MuiOutlinedInput-root": {
                                          "& fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                          "&:hover fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                        },
                                      }}
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      label="New Password"
                                      name="newPassword"
                                      type="password"
                                      autoComplete="new-password"
                                      onChange={handleChange}
                                      value={values.newPassword}
                                      error={
                                        !!(
                                          errors?.newPassword &&
                                          touched?.newPassword
                                        )
                                      }
                                      helperText={
                                        errors.newPassword &&
                                        touched.newPassword &&
                                        errors.newPassword
                                      }
                                      sx={{
                                        fontSize: "1.2rem",
                                        "& .MuiOutlinedInput-root": {
                                          "& fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                          "&:hover fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "#A9A9A9",
                                          },
                                        },
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            </CardContent>

                            <Divider sx={{ backgroundColor: "black" }} />

                            <CardActions sx={{ justifyContent: "center" }}>
                              <Button
                                variant="contained"
                                type="submit"
                                sx={{
                                  color: colors.white,
                                  backgroundColor: colors.secondPrimary,
                                  "&:hover": {
                                    backgroundColor: colors.secondPrimary,
                                  },
                                  width: 150,
                                }}
                              >
                                Reset
                              </Button>
                            </CardActions>
                          </Card>
                        </Form>
                      )}
                    </Formik>
                  </Grid>
                </Grid>
              </div>
            </Stack>
          </Container>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

VerifyAndResetPasswordModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default VerifyAndResetPasswordModal;
